import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Target, 
  Eye, 
  Shield, 
  Clock, 
  CheckCircle, 
  Users, 
  Award,
  Lock,
  MapPin,
  Zap,
  ArrowRight,
  Linkedin,
  Mail
} from 'lucide-react';

// Leadership Data
const leaders = [
  {
    name: 'Dr. Deepak Arya',
    position: 'Founder',
    bio: 'A visionary leader with extensive experience in risk assessment and verification services. Dr. Arya founded Aarambh with a mission to bring integrity and trust to the hiring ecosystem.',
    expertise: ['Strategic Vision', 'Industry Expertise', 'Ethical Leadership'],
  },
  {
    name: 'Prince Mishra',
    position: 'Managing Director',
    bio: 'Prince leads the operational excellence at Aarambh, ensuring that every verification meets the highest standards of accuracy and compliance.',
    expertise: ['Operations Management', 'Process Excellence', 'Client Relations'],
  },
];

// Why Choose Us Data
const whyChooseUs = [
  {
    icon: CheckCircle,
    title: 'High Accuracy & Data Integrity',
    description: 'Our multi-layered verification process ensures 99.9% accuracy in all reports.',
  },
  {
    icon: Clock,
    title: 'Defined Turnaround Times',
    description: 'We commit to clear TATs and deliver on time, every time.',
  },
  {
    icon: Shield,
    title: 'Ethical & Transparent Operations',
    description: 'Complete transparency in our processes with ethical business practices.',
  },
  {
    icon: Lock,
    title: 'Secure Data Handling',
    description: 'Enterprise-grade security for all sensitive verification data.',
  },
  {
    icon: MapPin,
    title: 'Field + Digital Verification',
    description: 'Hybrid model combining on-ground verification with digital checks.',
  },
  {
    icon: Zap,
    title: 'Custom Solutions',
    description: 'Tailored verification packages for complex requirements.',
  },
];

// Values Data
const values = [
  {
    icon: Target,
    title: 'Accuracy',
    description: 'Every verification is thorough and precise.',
  },
  {
    icon: Clock,
    title: 'Speed',
    description: 'Quick turnaround without compromising quality.',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'Ethical practices at every step.',
  },
  {
    icon: Users,
    title: 'Trust',
    description: 'Building lasting relationships.',
  },
];

export default function About() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-28" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4" />
              About Aarambh India Service
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">
              Your Trusted Partner in Background Verification
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              We take this opportunity to introduce ourselves as a growing and reliable Background Verification 
              and Risk Assessment organization, committed to delivering accurate, compliant, and timely 
              verification solutions to corporates, staffing firms, insurers, and enterprises across India.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="about-introduction">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Who We Are
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  At <span className="font-semibold text-slate-900">Aarambh India Services Pvt. Ltd.</span>, 
                  we specialize in end-to-end background verification and insurance verification services, 
                  designed to support informed decision-making, mitigate organizational risk, and ensure 
                  regulatory compliance.
                </p>
                <p>
                  Our operations are driven by a structured pan-India field network, strong process governance, 
                  and strict adherence to confidentiality and data security standards. We understand that 
                  verification is not just a process—it's the foundation of trust between organizations 
                  and their stakeholders.
                </p>
                <p>
                  From employee background checks to insurance claim verification, we provide comprehensive 
                  solutions that help organizations make confident decisions while protecting their reputation 
                  and ensuring compliance with regulatory requirements.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {values.map((value, index) => (
                <div 
                  key={index} 
                  className="bg-slate-50 rounded-xl p-6 text-center card-hover"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{value.title}</h3>
                  <p className="text-sm text-slate-500">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 lg:py-28 bg-slate-900 text-white" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-slate-800 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-slate-300 leading-relaxed">
                To build trust in hiring and verification through accuracy, speed, ethical practices, 
                and compliance-first execution. We are committed to empowering organizations with 
                reliable information that enables confident decision-making.
              </p>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-slate-300 leading-relaxed">
                To become India's most trusted verification partner, known for our commitment to 
                accuracy, innovation, and ethical business practices. We envision a future where 
                every hiring decision is backed by verified, reliable information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our Leadership
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Led by industry veterans committed to excellence and ethical business practices
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {leaders.map((leader, index) => (
              <Card key={index} className="border-slate-100 card-hover">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full bg-slate-900 text-white flex items-center justify-center text-2xl font-bold">
                      {leader.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{leader.name}</h3>
                      <p className="text-emerald-600 font-medium">{leader.position}</p>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6 leading-relaxed">{leader.bio}</p>
                  <div className="flex flex-wrap gap-2">
                    {leader.expertise.map((skill, i) => (
                      <span 
                        key={i} 
                        className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-slate-50" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Aarambh
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              What sets us apart in the verification industry
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-slate-100 card-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-slate-900" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how our verification services can add value to your organization.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-base"
              data-testid="about-contact-btn"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
